import { jsx as _jsx } from "react/jsx-runtime";
import * as d3 from "d3";
import { format } from "date-fns";
import React, { useEffect, useRef } from "react";
import { useAssetPowerSocTrendsQuery } from "../../services/query/project-asset-details.query";
import useAppConfigStore from "../../store/useAppConfigStore";
const CHART_MARGIN = { bottom: 60, left: 80, right: 80, top: 50 };
const TRENDS_QUERY_INTERVAL_MINUTES = 60;
const SOC_MAX = 100;
const DEFAULT_MAX_POWER = 100;
const AXIS_TICK_COUNT = 5;
const X_AXIS_HOUR_INTERVAL = 2;
const LINE_STROKE_WIDTH = 2;
const DATA_POINT_RADIUS = 3;
const AXIS_FONT_SIZE = "12px";
const LABEL_FONT_SIZE = "14px";
const LEGEND_FONT_SIZE = "11px";
const AXIS_TEXT_COLOR = "grey";
const LABEL_TEXT_COLOR = "black";
const LABEL_FONT_WEIGHT = "500";
const LABEL_FONT_FAMILY = "Sans-serif";
const COLOR_SOC = "#1AC69E";
const COLOR_POWER = "#FB5A00";
const GRID_LINE_COLOR = "#e0e0e0";
const GRID_STROKE_WIDTH = 0.5;
const GRID_DASH_ARRAY = "2,2";
const LEGEND_X_OFFSET = 200;
const LEGEND_Y_OFFSET = -30;
const LEGEND_ITEM_SPACING = 100;
const LEGEND_TEXT_X = 18;
const LEGEND_RECT_HEIGHT = 2;
const LEGEND_RECT_WIDTH = 12;
const X_LABEL_Y_OFFSET = 20;
const Y_LABEL_LEFT_OFFSET = 20;
const Y_LABEL_RIGHT_OFFSET = 30;
const POWER_TICK_COUNT = 5;
const LEGEND_DATA = [
    { color: COLOR_SOC, label: "SOC (%)" },
    { color: COLOR_POWER, label: "ESS Power (kW)" }
];
const AssetDetailsLineChart = ({ assetId, height, projectId, width }) => {
    const svgRef = useRef(null);
    const margin = CHART_MARGIN;
    const currentDay = new Date();
    const formattedTodayDate = format(currentDay, "yyyy-MM-dd");
    const { appConfig } = useAppConfigStore((state) => ({
        appConfig: state.appConfig
    }));
    const { data: { assetTrendsDetails = [] } = {} } = useAssetPowerSocTrendsQuery(projectId, formattedTodayDate, assetId, TRENDS_QUERY_INTERVAL_MINUTES);
    useEffect(() => {
        drawChart();
    }, [assetId, projectId, assetTrendsDetails]);
    const drawChart = () => {
        // Clear previous content
        d3.select(svgRef.current).selectAll("*").remove();
    };
    if (!assetTrendsDetails.length)
        return (_jsx("div", { className: "flex justify-center items-center h-full min-h-[40vh]", children: _jsx("span", { children: "No data available for the selected asset." }) }));
    const assetTrendsData = assetTrendsDetails !== undefined ? assetTrendsDetails[0].trends : [];
    /**
     * Parses timestamp strings into Date objects for D3.js chart rendering.
     *
     * For projects with showCestTimezone enabled (Malaga, Tiwa Dyke, etc.):
     * - API sends timestamps in project's local timezone (e.g., "00:00:00" = midnight Amsterdam)
     * - We parse them using UTC (Date.UTC) to create timezone-neutral Date objects
     * - ALL users worldwide see the same times (project timezone, not their browser's)
     * - Example: "00:00:00" displays as "12 AM" for users in Tokyo, New York, London, etc.
     *
     * Why UTC?: Using Date.UTC ensures the absolute moment in time is identical for all users.
     * Without UTC, new Date(2025, 11, 18, 0, 0, 0) would represent different absolute moments
     * in Tokyo (UTC+9) vs New York (UTC-5), causing the chart to show different times.
     *
     * Supports two formats:
     * 1. Time-only: "00:00:00", "15:30:45" (Malaga project)
     * 2. Full timestamp: "2025-12-18T00:00:00.000" (Tiwa Dyke project)
     *
     * @param timestampStr - Timestamp string from API
     * @returns Date object with timezone-neutral representation (using UTC)
     */
    const parseTimestamp = (timestampStr) => {
        if (!timestampStr)
            return new Date();
        // When showCestTimezone is TRUE:
        // API sends timestamps in project's local timezone (Amsterdam, London, etc.)
        // We display these times as-is, without timezone conversion
        if (appConfig?.release_1_1?.showCestTimezone) {
            try {
                let datePart;
                let timePart;
                // Handle time-only format: "00:00:00" or "15:30:45"
                if (!timestampStr.includes("T")) {
                    // For time-only format, use the same date that was sent in the API query
                    // (formattedTodayDate) to ensure consistency between the query date and parsed timestamps.
                    // The API query date is based on the user's browser date, and time-only timestamps
                    // are returned for that specific date, so we combine them accordingly.
                    datePart = formattedTodayDate;
                    timePart = timestampStr;
                }
                else {
                    // Full timestamp format: "2025-12-18T00:00:00.000"
                    [datePart, timePart] = timestampStr.split("T");
                }
                // Parse components directly without timezone conversion
                const [year, month, day] = datePart.split("-").map(Number);
                const [hours, minutes, seconds] = timePart.split(":").map((v) => Math.floor(Number(v)));
                // Create Date object using UTC to ensure timezone-neutral representation
                // This ensures ALL users worldwide see the same time values on the chart
                // e.g., "00:00:00" displays as midnight (12 AM) for everyone, not shifted by their timezone
                return new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
            }
            catch (error) {
                return new Date();
            }
        }
        // Legacy mode: Parse as local browser time
        return new Date(timestampStr);
    };
    // Expect full timestamp in ISO format from API when showCestTimezone flag is true
    const trendsData = appConfig?.release_1_1?.showCestTimezone
        ? assetTrendsData
        : assetTrendsData.map((data) => {
            let timestamp = data.timestamp || "";
            if (!timestamp.includes("T")) {
                const todayPrefix = new Date().toISOString().split("T")[0] + "T";
                timestamp = todayPrefix + timestamp;
            }
            timestamp = timestamp.replace(/Z$/, "");
            return {
                ...data,
                timestamp
            };
        });
    // Create powerData array
    const powerData = trendsData
        .filter((data) => data.power !== undefined) // Filter out objects without power value
        .map((data) => ({
        parsedDate: parseTimestamp(data.timestamp || ""),
        timestamp: data.timestamp,
        value: data.power ?? undefined // Default to undefined if power is undefined
    }));
    // Create socData array
    const socData = trendsData
        .filter((data) => data.soc !== undefined)
        .map((data) => ({
        parsedDate: parseTimestamp(data.timestamp || ""),
        timestamp: data.timestamp,
        value: data.soc ?? undefined // Default to undefined if power is undefined
    }));
    // Setup SVG
    const svg = d3
        .select(svgRef.current)
        .style("max-width", "100%")
        .style("min-width", "100%")
        .style("max-height", "100%")
        .style("position", "relative")
        .style("overflow", "auto")
        .attr("viewBox", `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`); // Add viewBox
    const chartArea = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);
    // Add clipPath to make the chart scrollable
    chartArea.append("clipPath").attr("id", "clip").append("rect").attr("width", width).attr("height", height);
    const clippedArea = chartArea.append("g").attr("clip-path", "url(#clip)");
    // Combine all parsed dates for domain calculation
    const allParsedDates = [...powerData.map((d) => d.parsedDate), ...socData.map((d) => d.parsedDate)];
    const xScale = d3
        .scaleTime()
        .domain(d3.extent(allParsedDates)) // Use pre-parsed dates
        .range([0, width]);
    const yScaleSOC = d3.scaleLinear().domain([0, SOC_MAX]).range([height, 0]);
    const maxPower = powerData.length > 0 ? d3.max(powerData, (d) => d.value) || DEFAULT_MAX_POWER : DEFAULT_MAX_POWER;
    const yScalePower = d3.scaleLinear().domain([0, maxPower]).range([height, 0]);
    // Create axes
    const xAxis = d3
        .axisBottom(xScale)
        .tickFormat((d) => d3.utcFormat("%I %p")(d))
        .ticks(d3.utcHour.every(X_AXIS_HOUR_INTERVAL));
    const yAxisLeft = d3.axisLeft(yScaleSOC).ticks(AXIS_TICK_COUNT);
    const yAxisRight = d3
        .axisRight(yScalePower)
        .ticks(AXIS_TICK_COUNT)
        .tickValues(d3.range(0, maxPower + 1, maxPower / POWER_TICK_COUNT));
    // Remove existing axes to avoid duplication
    chartArea.select(".x-axis").remove();
    // Add X-axis
    chartArea
        .append("g")
        .attr("class", "x-axis")
        .attr("transform", `translate(0, ${height})`)
        .call(xAxis)
        .selectAll("text")
        .style("text-anchor", "middle")
        .attr("font-size", AXIS_FONT_SIZE)
        .attr("fill", AXIS_TEXT_COLOR);
    chartArea
        .select(".x-axis .domain") // Hide the axis line
        .attr("stroke", "none");
    // Add Y-axis (left) for SOC
    chartArea
        .append("g")
        .attr("class", "y-axis-left")
        .call(yAxisLeft)
        .attr("font-size", AXIS_FONT_SIZE)
        .selectAll("text")
        .attr("fill", AXIS_TEXT_COLOR);
    chartArea
        .select(".y-axis-left .domain") // Hide the axis line
        .attr("stroke", "none");
    // Add Y-axis (right) for Power
    chartArea
        .append("g")
        .attr("class", "y-axis-right")
        .attr("transform", `translate(${width}, 0)`)
        .call(yAxisRight)
        .attr("font-size", AXIS_FONT_SIZE)
        .selectAll("text")
        .attr("fill", AXIS_TEXT_COLOR);
    chartArea
        .select(".y-axis-right .domain") // Hide the axis line
        .attr("stroke", "none");
    // Add X-axis label
    chartArea
        .append("text")
        .attr("fill", LABEL_TEXT_COLOR)
        .attr("transform", `translate(${width / 2}, ${height + margin.bottom - X_LABEL_Y_OFFSET})`)
        .style("text-anchor", "middle")
        .style("font", LABEL_FONT_FAMILY)
        .style("font-size", LABEL_FONT_SIZE)
        .style("font-weight", LABEL_FONT_WEIGHT)
        .text("Timeline");
    // Y-axis labels
    chartArea
        .append("text")
        .attr("transform", "rotate(-90)")
        .attr("y", 0 - margin.left + Y_LABEL_LEFT_OFFSET)
        .attr("x", 0 - height / 2)
        .attr("dy", "1em")
        .style("text-anchor", "middle")
        .style("font", LABEL_FONT_FAMILY)
        .style("font-size", LABEL_FONT_SIZE)
        .style("font-weight", LABEL_FONT_WEIGHT)
        .attr("fill", LABEL_TEXT_COLOR)
        .text("SOC (%)");
    chartArea
        .append("text")
        .attr("transform", "rotate(-90)")
        .attr("y", width + margin.right - Y_LABEL_RIGHT_OFFSET)
        .attr("x", 0 - height / 2)
        .attr("dy", "1em")
        .style("text-anchor", "middle")
        .style("font-family", LABEL_FONT_FAMILY)
        .style("font-size", LABEL_FONT_SIZE)
        .style("font-weight", LABEL_FONT_WEIGHT)
        .attr("fill", LABEL_TEXT_COLOR)
        .text("ESS Power (kW)");
    // Create SOC line
    const lineSOC = d3
        .line()
        .x((d) => xScale(d.parsedDate)) // Use pre-parsed date
        .y((d) => yScaleSOC(d.value)) // Use yScaleSOC for soc values
        .curve(d3.curveLinear);
    if (socData.length > 1) {
        clippedArea
            .append("path")
            .datum(socData)
            .attr("fill", "none")
            .attr("stroke", COLOR_SOC)
            .attr("stroke-width", LINE_STROKE_WIDTH)
            .attr("d", lineSOC);
        // Add circles to connect every point
        socData.forEach((dataPoint) => {
            clippedArea
                .append("circle")
                .attr("cx", xScale(dataPoint.parsedDate)) // Use pre-parsed date
                .attr("cy", yScaleSOC(dataPoint.value)) // Use yScaleSOC for SOC value
                .attr("r", DATA_POINT_RADIUS)
                .attr("fill", COLOR_SOC); // Color for the point
        });
    }
    else if (socData.length === 1) {
        // Render a single point instead of a line
        clippedArea
            .append("circle")
            .attr("cx", xScale(socData[0].parsedDate))
            .attr("cy", yScaleSOC(socData[0].value))
            .attr("r", DATA_POINT_RADIUS)
            .attr("fill", COLOR_SOC);
    }
    // Create Power line
    const linePower = d3
        .line()
        .x((d) => xScale(d.parsedDate)) // Use pre-parsed date
        .y((d) => yScalePower(d.value)) // Use yScalePower for power values
        .curve(d3.curveLinear);
    if (powerData.length > 1) {
        clippedArea
            .append("path")
            .datum(powerData)
            .attr("fill", "none")
            .attr("stroke", COLOR_POWER)
            .attr("stroke-width", LINE_STROKE_WIDTH)
            .attr("d", linePower);
        // Add circles to connect every point
        powerData.forEach((dataPoint) => {
            clippedArea
                .append("circle")
                .attr("cx", xScale(dataPoint.parsedDate)) // Use pre-parsed date
                .attr("cy", yScalePower(dataPoint.value)) // Use yScaleSOC for SOC value
                .attr("r", DATA_POINT_RADIUS)
                .attr("fill", COLOR_POWER);
        });
    }
    else if (powerData.length === 1) {
        // Render a single point instead of a line
        clippedArea
            .append("circle")
            .attr("cx", xScale(powerData[0].parsedDate))
            .attr("cy", yScalePower(powerData[0].value))
            .attr("r", DATA_POINT_RADIUS)
            .attr("fill", COLOR_POWER);
    }
    // Add legend on top right
    const legend = chartArea
        .append("g")
        .attr("class", "legend")
        .attr("transform", `translate(${width - LEGEND_X_OFFSET}, ${LEGEND_Y_OFFSET})`);
    const legendItems = legend
        .selectAll(".legend-item")
        .data(LEGEND_DATA)
        .enter()
        .append("g")
        .attr("class", "legend-item")
        .attr("transform", (d, i) => `translate(${i * LEGEND_ITEM_SPACING}, 0)`);
    legendItems
        .append("rect")
        .attr("width", LEGEND_RECT_WIDTH)
        .attr("height", LEGEND_RECT_HEIGHT)
        .attr("fill", (d) => d.color);
    legendItems
        .append("text")
        .attr("x", LEGEND_TEXT_X)
        .attr("y", 1)
        .attr("dy", "0.35em")
        .style("font-size", LEGEND_FONT_SIZE)
        .style("font-weight", "normal")
        .text((d) => d.label);
    // Add grid lines for better readability
    chartArea
        .selectAll(".grid-line-y")
        .data(yScaleSOC.ticks(5))
        .enter()
        .append("line")
        .attr("class", "grid-line-y")
        .attr("x1", 0)
        .attr("x2", width)
        .attr("y1", (d) => yScaleSOC(d))
        .attr("y2", (d) => yScaleSOC(d))
        .attr("stroke", GRID_LINE_COLOR)
        .attr("stroke-width", GRID_STROKE_WIDTH)
        .attr("stroke-dasharray", GRID_DASH_ARRAY);
    return (_jsx("div", { children: _jsx("svg", { "data-test": "asset-details-linechart-svg", ref: svgRef }) }));
};
export default AssetDetailsLineChart;
